import { memo, useCallback, useContext } from "react";
import { type NumberFormatValues, NumericFormat, type SourceInfo } from "react-number-format";

import { cn } from "@btnet/utilities-core";

import { CurrencyLocale } from "helpers/AppProvider.types";
import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { round } from "utilities/math/math";
import { type KeyOfOrString } from "utilities/type/PropsOfType";

import {
    type IInputDebouncingContext,
    useDebouncedOnChange,
} from "commonComponents/utilities/Input/InputDebouncingContext";
import { type ReadOnlyShouldTruncate } from "commonComponents/utilities/TextTruncate/TextTruncate.type";
import {
    isReadOnly,
    shouldTruncate,
} from "commonComponents/utilities/TextTruncate/TextTruncate.utility";

import "./Percentage.less";

import { CustomInput } from "../Currency/Currency";
import { PercentageDisplay } from "../PercentageDisplay/PercentageDisplay";

interface IPercentageProps<FormValues> {
    id: KeyOfOrString<FormValues>;

    "data-testid": string;

    /** pass formik setFieldValue */
    onChange: (
        field: string,
        value: number | undefined,
        shouldValidate?: boolean,
        values?: NumberFormatValues,
    ) => void;

    /** pass formik setFieldTouched */
    onBlur: (field: string, touched: boolean) => void;

    onFocus?: () => void;

    value: number | undefined | null;

    /** @default "default" */
    size?: "large" | "default" | "small";

    /** @default false */
    allowNegative?: boolean;

    /** @default 2 */
    decimalScale?: number;

    /** @default false */
    disabled?: boolean;

    /** @default true */
    defaultToZero?: boolean;

    /** @default none (inclusive)*/
    max?: number;

    /** @default none (inclusive)*/
    min?: number;

    style?: React.CSSProperties;

    /** @default false */
    readOnly?: ReadOnlyShouldTruncate;

    /** Percentage will not reflect builder culture and instead show in US culture */
    forceUSCulture?: boolean;

    /** @default true */
    showSuffix?: boolean;

    autoFocus?: boolean;

    className?: string;

    /**
     * Sets the debounce context and limits. Should only be set in edge cases.
     */
    debouncingContext?: IInputDebouncingContext;
    placeholder?: string;
    onPressEnter?: React.KeyboardEventHandler<HTMLInputElement>;
}

const PercentageInternal = track((props) => ({
    element: "Percentage Input",
    uniqueId: props["data-testid"],
}))(function <FormValues = undefined>({
    id,
    style,
    disabled = false,
    onChange,
    onBlur,
    value,
    defaultToZero = true,
    max = 1_000_000_000,
    min = -1_000_000_000,
    allowNegative = false,
    decimalScale = 2,
    "data-testid": dataTestId,
    readOnly = false,
    showSuffix = true,
    autoFocus,
    className,
    debouncingContext,
    tracking,
    placeholder,
    onPressEnter,
    ...otherProps
}: IPercentageProps<FormValues> & ITrackingProp) {
    const [debouncedValue, debouncedChange] = useDebouncedOnChange<typeof value, FormValues>(
        value,
        onChange,
        debouncingContext,
    );
    const builderInfo = useContext(BuilderInfoContext);

    const builderCurrency =
        otherProps.forceUSCulture || !builderInfo
            ? CurrencyLocale.default
            : builderInfo.locale.currencyLocale;

    function isAllowed(values: NumberFormatValues): boolean {
        const floatValue = values.floatValue ?? 0;
        return min <= floatValue && floatValue <= max;
    }

    function handleChange(values: NumberFormatValues, { source }: SourceInfo) {
        if (source === "event") {
            debouncedChange(id, values.floatValue, true, values);
        }
    }

    function handleBlur() {
        onBlur(id as string, true);
        tracking?.trackEvent({ event: "InputBlur" });
    }

    function handleFocus(event: React.FocusEvent<HTMLInputElement>) {
        event.target.select();
        tracking?.trackEvent({ event: "InputFocus" });
        otherProps.onFocus?.();
    }

    const defaultValue = defaultToZero ? 0 : "";
    const roundedValue =
        debouncedValue !== undefined && typeof debouncedValue === "number" && !isNaN(debouncedValue)
            ? round(debouncedValue, decimalScale)
            : defaultValue;
    const customInputParams = {
        suffixOverride: showSuffix ? "%" : "",
        style: style,
        defaultClassName: cn("PercentageInput", className),
        "data-testid": dataTestId,
    };

    const handlePressEnter = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (onPressEnter) {
                tracking?.trackEvent({ event: "Hotkey", value: "Enter" });
                onPressEnter(event);
            }
        },
        [onPressEnter, tracking],
    );

    if (isReadOnly(readOnly)) {
        return (
            <PercentageDisplay
                value={roundedValue || 0}
                decimalScale={decimalScale}
                data-testid={dataTestId}
                shouldTruncate={shouldTruncate(readOnly)}
            />
        );
    }

    return (
        <NumericFormat
            id={id as string}
            defaultValue={defaultValue}
            value={roundedValue}
            displayType="input"
            allowNegative={allowNegative}
            decimalScale={decimalScale}
            fixedDecimalScale={true}
            disabled={disabled}
            onValueChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            isAllowed={isAllowed}
            thousandSeparator={builderCurrency.groupSeparator}
            decimalSeparator={builderCurrency.decimalSeparator}
            thousandsGroupStyle={builderCurrency.thousandsGroupStyle}
            customInput={CustomInput}
            autoFocus={autoFocus}
            placeholder={placeholder}
            onPressEnter={handlePressEnter}
            {...customInputParams}
        />
    );
});

export const Percentage = memo(PercentageInternal) as typeof PercentageInternal;
